import Head from "next/head";
import React, { Fragment, useEffect, useState } from "react";
import { Chart } from "react-chartjs-2";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import * as ChartGeo from "chartjs-chart-geo";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon, CheckIcon } from "@heroicons/react/20/solid";
import { useRouter } from "next/router";
import { Combobox, Transition } from "@headlessui/react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import useSWRInfinite from "swr/infinite";
import useSWR from "swr";
import AnimalCard from "components/animalCard";
import BlankCard from "components/blankCard";
import Sidebar from "components/sidebar";
import SearchFilter from "components/filter";

const fetcher = (url) => fetch(url, { credentials: "include" }).then((res) => res.json());

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function Home() {
	const [country, setCountry] = useState(null);
	const [data, setData] = useState(null);
	const [countryList, setCountryList] = useState([]);
	const [countryListSorted, setCountryListSorted] = useState([]);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [riskFilters, setRiskFilters] = useState([]);
	const [orderFilters, setOrderFilters] = useState([]);
	const [habitatFilters, setHabitatFilters] = useState([]);
	const [queryString, setQueryString] = useState("");
	const [queryStringC, setQueryStringC] = useState("");

	const getKey = (pageIndex, previousPageData) => {
		if ((previousPageData && !previousPageData.nextOffset) || !country) return null;
		if (pageIndex === 0) return `/api/search?${queryStringC}&limit=8&scoreSort=true`;
		return `/api/search?offset=${previousPageData.nextOffset}&limit=8&${queryStringC}&scoreSort=true`;
	};

	const { data: animalData, error, size, setSize } = useSWRInfinite(getKey, fetcher);
	const [animals, setAnimals] = useState(
		animalData && animalData.length > 0 && animalData[0].data ? animalData[0].data : []
	);
	const { data: animalCountData, error: animalCountError } = useSWR(`/api/initial?${queryString}`, fetcher);
	const [animalCount, setAnimalCount] = useState(animalCountData || {});

	useEffect(() => {
		const params = {};
		if (riskFilters.length !== 0) params.risk = riskFilters.join();
		if (orderFilters.length !== 0) params.order = orderFilters.join();
		if (habitatFilters.length !== 0) params.habitat = habitatFilters.join();
		setQueryString(new URLSearchParams(params).toString());
		params.country = country;
		setQueryStringC(new URLSearchParams(params).toString());
	}, [riskFilters, orderFilters, habitatFilters, country]);

	useEffect(() => {
		if (!animalData) return;
		setAnimals(animalData.reduce((prev, page) => prev.concat(page.data ?? []), []));
	}, [animalData]);

	useEffect(() => {
		if (animalCountData) setAnimalCount(animalCountData || {});
	}, [animalCountData]);

	const handleScroll = (evt) => {
		if (
			window.innerWidth <= 1535 &&
			window.innerHeight + window.scrollY >= document.body.scrollHeight - 500 &&
			!((!animalData && !error) || (size > 0 && animalData && typeof animalData[size - 1] === "undefined"))
		) {
			console.log("LOADING MORE = SMALL");
			setSize(size + 1);
		} else if (
			document.getElementById("scrollRef") &&
			evt.target.scrollTop >= document.getElementById("scrollRef").scrollHeight - 1500 &&
			!((!animalData && !error) || (size > 0 && animalData && typeof animalData[size - 1] === "undefined"))
		) {
			setSize(size + 1);
			console.log("LOADING MORE");
		}
	};

	useEffect(() => {
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	});

	const [query, setQuery] = useState("");

	const filteredCountry =
		query === ""
			? countryListSorted
			: countryList.filter((c) =>
					c.toLowerCase().replace(/\s+/g, "").includes(query.toLowerCase().replace(/\s+/g, ""))
			  );

	useEffect(() => {
		fetch("https://unpkg.com/world-atlas/countries-50m.json")
			.then((r) => r.json())
			.then((json) => {
				const countries = ChartGeo.topojson.feature(json, json.objects.countries).features;
				setCountryList(countries.map((d) => d.properties.name));
				setCountryListSorted(countries.map((d) => d.properties.name).sort());

				setData({
					labels: countries.map((d) => d.properties.name),
					datasets: [
						{
							label: "Countries",
							data: countries.map((d) => ({ feature: d, value: animalCount[d.properties.name] || 0 })),
						},
					],
				});
			});
	}, [animalCount]);

	const router = useRouter();
	useEffect(() => {
		if (router.query.country) {
			if (countryList.includes(router.query.country)) {
				setCountry(router.query.country);
			}
		}
	}, [router.query.country, countryList]);
	useEffect(() => {
		if (country && router.query.country !== country) router.push(`/?country=${country}`, undefined, { shallow: true });
	}, [country, router, router.query.country]);

	const options = {
		onClick(evt, element) {
			if (element.length > 0) setCountry(countryList[element[0].index]);
		},
		showOutline: true,
		showGraticule: true,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				backgroundColor: "#2c2f34",
				bodyFontColor: "#FFF",
				titleMarginBottom: 10,
				titleFontColor: "#FFF",
				titleFontSize: 14,
				borderColor: "#2c2f34",
				borderWidth: 1,
				displayColors: false,
				intersect: false,
				callbacks: {
					title(context) {
						return countryList[context[0].dataIndex];
					},
					label(context) {
						const c = countryList[context.dataIndex];
						if (!animalCount[c]) return "No Infectious Species";
						return `${animalCount[c]} Infectious Animal${animalCount[c] === 1 ? "" : "s"}`;
					},
				},
			},
		},
		scales: {
			xy: {
				projection: "equalEarth",
			},
		},
	};

	return (
		<div>
			<Head>
				<title>Map | Cary Dashboard</title>
				<meta name="description" content="Cary Dashboard" />
			</Head>

			<div className="overflow-auto bg-gray-100">
				<SkeletonTheme>
					<Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} page={"World Map"} />
					<div className="flex flex-1 flex-col md:pl-64">
						<Combobox value={country} onChange={setCountry} nullable>
							<div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white shadow">
								<button
									type="button"
									className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-cary-400 md:hidden"
									onClick={() => setSidebarOpen(true)}>
									<span className="sr-only">Open sidebar</span>
									<Bars3Icon className="h-6 w-6" aria-hidden="true" />
								</button>
								<div className="flex flex-1 justify-between px-4">
									<div className="flex flex-1">
										<form className="flex w-full md:ml-0" action="#" method="GET">
											<label htmlFor="search-field" className="sr-only">
												Search
											</label>
											<div className="relative w-full text-gray-400 focus-within:text-gray-600">
												<Combobox.Button className="absolute inset-y-0 left-0 flex items-center">
													<MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
												</Combobox.Button>
												<Combobox.Input
													onChange={(event) => setQuery(event.target.value)}
													displayValue={(c) => c}
													placeholder="Search"
													className="block h-full w-full border-transparent py-2 pl-8 pr-3 text-gray-900 placeholder-gray-500 focus:border-transparent focus:placeholder-gray-400 focus:outline-none focus:ring-0 sm:text-sm"
												/>
												<Transition
													as={Fragment}
													leave="transition ease-in duration-100"
													leaveFrom="opacity-100"
													leaveTo="opacity-0"
													afterLeave={() => setQuery("")}>
													<Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
														{filteredCountry.length === 0 && query !== "" ? (
															<div className="relative cursor-default select-none py-2 px-4 text-gray-700">
																Nothing found.
															</div>
														) : (
															filteredCountry.map((c) => (
																<Combobox.Option
																	key={c}
																	className={({ active }) =>
																		`relative cursor-default select-none py-2 pl-10 pr-4 ${
																			active ? "bg-cary-400 text-white" : "text-gray-900"
																		}`
																	}
																	value={c}>
																	{({ selected, active }) => (
																		<>
																			<span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
																				{c}
																			</span>
																			{selected ? (
																				<span
																					className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
																						active ? "text-white" : "text-cary-400"
																					}`}>
																					<CheckIcon className="h-5 w-5" aria-hidden="true" />
																				</span>
																			) : null}
																		</>
																	)}
																</Combobox.Option>
															))
														)}
													</Combobox.Options>
												</Transition>
											</div>
										</form>
									</div>
								</div>
							</div>
						</Combobox>

						<main className="flex-1 bg-gray-100">
							<div className="mx-auto">
								<div className="2xl:flex 2xl:flex-wrap-reverse">
									<div className="m-none w-full flex-none pt-10 2xl:m-auto 2xl:w-7/12">
										<div className="mx-2 text-center md:mx-4 xl:mx-10">
											<div className="mb-10">
												<h1 className="text-3xl font-bold lg:text-4xl 2xl:text-3xl">
													This is a fancy dashboard title....
												</h1>
												<h2 className="text-">
													This is a subheading, with more information on what the dashboard can do.
												</h2>
											</div>
											{data && <Chart type="choropleth" options={options} data={data} />}
										</div>
									</div>

									<div
										className="min-height-100vh height-100vh-2xl w-full flex-none py-6 2xl:w-5/12 2xl:overflow-auto 2xl:pt-24"
										onScroll={handleScroll}
										id="scrollRef">
										<h1 className="pt-8 text-center text-2xl font-bold">
											{!animalCountData && !animalCountError && !country ? (
												<Skeleton height={35} width={300} />
											) : (
												country || "No Country Selected"
											)}
										</h1>

										<SearchFilter
											riskFilters={riskFilters}
											setRiskFilters={setRiskFilters}
											orderFilters={orderFilters}
											setOrderFilters={setOrderFilters}
											habitatFilters={habitatFilters}
											setHabitatFilters={setHabitatFilters}
											page={"database"}>
											{!animalCountData && !animalCountError ? (
												<Skeleton height={20} width={200} />
											) : (
												<>
													{country && <>{animalData?.[0]?.totalResults ?? "..."} Infectious Species Found</>}
													{!country && <>Select a country to continue.</>}
												</>
											)}
										</SearchFilter>

										<div className="mx-6 2xl:mx-12">
											{(animalCountData || animalCountError) && animals && animals.length > 0 && (
												<>
													<ul
														role="list"
														className="mt-4 grid grid-cols-1 place-content-center gap-6 lg:grid-cols-2 2xl:grid-cols-1">
														{animals.map((animal) => (
															<AnimalCard key={animal.name} animal={animal} setCountry={setCountry} />
														))}
													</ul>
												</>
											)}

											{!animalCountData && !animalCountError && (
												<>
													<ul
														role="list"
														className="mt-4 grid grid-cols-1 place-content-center gap-6 lg:grid-cols-2 2xl:grid-cols-1">
														<BlankCard key={"blank-1"} />
														<BlankCard key={"blank-2"} />
													</ul>
												</>
											)}

											{size * 8 < animalData?.[0]?.totalResults && (
												<button
													onClick={() => setSize(size + 1)}
													className="relative my-4 -mr-px inline-flex w-full flex-1 items-center justify-center rounded-lg rounded-bl-lg border-2 border-transparent bg-gray-200 py-2 px-4 text-center text-sm font-medium text-gray-800">
													{(!animalData && !error) ||
													(size > 0 && animalData && typeof animalData[size - 1] === "undefined") ? (
														<>
															<svg
																className="mr-2 h-5 w-5 animate-spin text-black"
																xmlns="http://www.w3.org/2000/svg"
																fill="none"
																viewBox="0 0 24 24">
																<circle
																	className="opacity-25"
																	cx="12"
																	cy="12"
																	r="10"
																	stroke="currentColor"
																	strokeWidth="4"></circle>
																<path
																	className="opacity-75"
																	fill="currentColor"
																	d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
															</svg>{" "}
															Loading
														</>
													) : (
														`Load More (Size: ${size})`
													)}
												</button>
											)}
										</div>
									</div>
								</div>
							</div>
						</main>
					</div>
				</SkeletonTheme>
			</div>
		</div>
	);
}
